import { i as isSpacingTokenType, e as isBorderRadiusTokenType, f as isBorderColorTokenType, g as isBackgroundColorTokenType, h as isTextColorTokenType, j as isBreakpointsTokenType, k as isShadowsTokenType, d as Spacing, B as BorderRadius, C as Colors, c as Shadows } from './typeGuard-bc66540d.js';
import { B as Breakpoints } from './breakpoints-d6ef9003.js';
import { L as LogUtility } from './log-a824be80.js';
import { R as ResponsiveFormatFacade } from './responsiveFormatFacade-438c3cd2.js';

/**
 * It logs a warning message to the console.
 *
 * @param {string} tokenType - The name of the token type.
 * @param {unknown} value - The value that was passed into the token.
 * @param {unknown} availableOptions - The available options for the token type.
 */
function logInvalidValueMessage(tokenType, value, availableOptions) {
  LogUtility.warn(`The ${tokenType} value ${value} is invalid, it must be a value of: `, availableOptions);
}
/**
 * Given a token and an array of modes, return a string with the token formatted with the appropriate mode
 *
 * @param {string} token - The name of the token.
 * @param {TokenMode[]} modes - TokenMode[]
 * @returns {string} The formatted token.
 */
function formatTokenWithModes(token, modes) {
  let formattedToken = '';
  if (modes.includes('dark')) {
    formattedToken += ` dark:v-dark-${token}`;
  }
  return formattedToken;
}
/**
 * If the token is a valid spacing token, return the CSS class string. Otherwise, log an error message
 *
 * @param {unknown} token - The value that was passed into the function.
 * @returns {string} The the CSS class string of the padding token.
 */
function formatAndValidPadding(token) {
  if (isSpacingTokenType(token)) {
    return `v-p-${token}`;
  }
  else {
    logInvalidValueMessage('padding', token, Object.keys(Spacing));
  }
}
/**
 * If the token is a valid border radius, return a string with the token prefixed with `v-rounded-`. Otherwise, log an
 * error message
 *
 * @param {unknown} token - The value passed in by the user.
 * @returns {string} The the CSS class string of the border radius token.
 */
function formatAndValidBorderRadius(token) {
  if (isBorderRadiusTokenType(token)) {
    return `v-rounded-${token}`;
  }
  else {
    logInvalidValueMessage('border-radius', token, Object.keys(BorderRadius));
  }
}
/**
 * If the token is a valid border color, return CSS class string of the border color. Otherwise, log an error message
 *
 * @param {unknown} token - The value passed in by the user.
 * @returns {string}  return the CSS class string of the border color
 */
function formatAndValidBorderColor(token) {
  if (isBorderColorTokenType(token)) {
    return `v-${token}`;
  }
  else {
    logInvalidValueMessage('border-color', token, Colors.filter(() => /^border-.+$/.test));
  }
}
/**
 * If the token is a valid background color, return it with the v- prefix. Otherwise, log an error
 *
 * @param {unknown} token - The value of the token.
 * @param {TokenMode[]} withModes - TokenMode[] = []
 * @returns {string} CSS class string for the background color token with modes supported.
 */
function formatAndValidBackgroundColor(token, withModes = []) {
  if (isBackgroundColorTokenType(token)) {
    return `v-${token}` + formatTokenWithModes(token, withModes);
  }
  else {
    logInvalidValueMessage('background-color', token, Colors.filter(() => /^border-.+$/.test));
  }
}
/**
 * If the token is a valid text color, return the token prefixed with `v-` and formatted with the given modes. Otherwise,
 * log an error
 *
 * @param {unknown} token - The token to format.
 * @param {TokenMode[]} withModes - TokenMode[] = []
 * @returns {string} A CSS class string that is either a valid color or a valid color with modes.
 */
function formatAndValidTextColor(token, withModes = []) {
  if (isTextColorTokenType(token)) {
    return `v-${token}` + formatTokenWithModes(token, withModes);
  }
  else {
    logInvalidValueMessage('text-color', token, Colors.filter(() => /^border-.+$/.test));
  }
}
/**
 * If the token is a valid breakpoint, return CSS class string for the flex breakpoint. Otherwise, log an error
 *
 * @param {unknown} token - The value of the breakpoint.
 * @returns {string} CSS class string for the flex breakpoint.
 */
function formatAndValidFlexBreakpoint(token) {
  if (isBreakpointsTokenType(token)) {
    return `vega-flex-${token}`;
  }
  else {
    logInvalidValueMessage('breakpoint', token, Object.keys(Breakpoints));
  }
}
/**
 * If the token is a breakpoint, return the breakpoint value. Otherwise, log an error and return 0
 *
 * @param {unknown} token - The value passed in by the user.
 * @returns {number} value of the breakpoint.
 */
function formatAndValidBreakpointSize(token) {
  if (isBreakpointsTokenType(token)) {
    return Breakpoints[token];
  }
  else {
    logInvalidValueMessage('breakpoint', token, Object.keys(Breakpoints));
  }
}
/**
 * If the token is a valid spacing token, return a string with the token name prefixed with `v-gap-`. Otherwise, log an
 * error message
 *
 * @param {unknown} token - The value passed in by the user.
 * @returns {string} CSS class string for the given gap token.
 */
function formatAndValidGap(token) {
  if (isSpacingTokenType(token)) {
    return `v-gap-${token}`;
  }
  else {
    logInvalidValueMessage('gap', token, Object.keys(Spacing));
  }
}
/**
 * If the token is a valid shadows token type, return the token with the prefix "v-". Otherwise, log an error message
 *
 * @param {string} token - The token to format.
 * @param {TokenMode[]} withModes - TokenMode[] = []
 * @returns {string} CSS class string of the shadow token with modes supported
 */
function formatAndValidShadow(token, withModes = []) {
  if (isShadowsTokenType(token)) {
    return `v-${token}` + formatTokenWithModes(token, withModes);
  }
  else {
    logInvalidValueMessage('shadows', token, Shadows);
  }
}
/**
 * It takes a token and a flag, and returns a string
 *
 * @typedef Flag key of ManagedResponsiveScopeInput
 * @param {ResponsiveType<ManagedResponsiveScopeInput[Flag]>} token - The token to format.
 * @param {Flag} flag - The flag that the token is being formatted for.
 * @returns {string} A string
 */
function formatAndValidResponsiveToken(token, flag) {
  if (token !== null && token !== undefined) {
    return ResponsiveFormatFacade.format(token, flag);
  }
}

export { formatAndValidGap as a, formatAndValidShadow as b, formatAndValidBreakpointSize as c, formatAndValidBackgroundColor as d, formatAndValidBorderColor as e, formatAndValidResponsiveToken as f, formatAndValidBorderRadius as g, formatAndValidFlexBreakpoint as h, formatAndValidPadding as i, formatAndValidTextColor as j };
